import streamSaver from 'streamsaver';
import { downloadStore } from 'store/downloadStore';

// same-origin mitm/sw. Default streamsaver.mitm은 jimmywarting.github.io로 잡혀 있어 CSP/장애 이슈 있음.
// public/mitm.html + public/sw.js 를 CRA가 서빙하므로 same-origin으로 강제.
if (typeof window !== 'undefined') {
  streamSaver.mitm = `${window.location.origin}/mitm.html`;
}

const safeFilename = (name: string): string =>
  name.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim();

const supportsStreamSaver = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (typeof WritableStream === 'undefined') return false;
  return true;
};

export interface DownloadItem {
  url: string;
  filename: string;
  label: string;
}

// -----------------------------------------------------------------------------
// File System Access API (Chrome/Edge). 사용자가 폴더 지정 시 그 안으로 스트리밍.
// -----------------------------------------------------------------------------

interface FileSystemWritableStreamLike {
  write(chunk: BufferSource | Blob | string): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}
interface FileSystemFileHandleLike {
  createWritable(): Promise<FileSystemWritableStreamLike>;
}
interface FileSystemDirectoryHandleLike {
  getFileHandle(
    name: string,
    options?: { create?: boolean }
  ): Promise<FileSystemFileHandleLike>;
}

const supportsDirectoryPicker = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';

const pickDirectory = async (): Promise<FileSystemDirectoryHandleLike | null> => {
  const w = window as {
    showDirectoryPicker?: (opts?: {
      mode?: 'read' | 'readwrite';
    }) => Promise<FileSystemDirectoryHandleLike>;
  };
  if (!w.showDirectoryPicker) return null;
  try {
    return await w.showDirectoryPicker({ mode: 'readwrite' });
  } catch (_e) {
    // 사용자 취소 (AbortError) 등 → null
    return null;
  }
};

const streamToFileHandle = async (
  url: string,
  fileHandle: FileSystemFileHandleLike,
  itemId: string,
  controller: AbortController
): Promise<void> => {
  const response = await fetch(url, { signal: controller.signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  if (!response.body) throw new Error('No response body');
  const lenHeader = response.headers.get('Content-Length');
  const totalBytes = lenHeader ? Number(lenHeader) : null;
  downloadStore.updateItem(itemId, { status: 'downloading', totalBytes });

  const writable = await fileHandle.createWritable();
  const reader = response.body.getReader();
  let received = 0;

  controller.signal.addEventListener('abort', () => {
    reader.cancel().catch(() => undefined);
    writable.abort?.().catch(() => undefined);
  });

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    // eslint-disable-next-line no-await-in-loop
    await writable.write(value);
    received += value.byteLength;
    downloadStore.updateItem(itemId, { receivedBytes: received });
  }
  await writable.close();
};

const downloadItemsToDirectory = async (
  items: DownloadItem[],
  dirHandle: FileSystemDirectoryHandleLike
): Promise<void> => {
  for (const item of items) {
    const controller = new AbortController();
    const storeItem = downloadStore.createItem(item.label, item.filename, () =>
      controller.abort()
    );
    try {
      // eslint-disable-next-line no-await-in-loop
      const fileHandle = await dirHandle.getFileHandle(safeFilename(item.filename), {
        create: true,
      });
      // eslint-disable-next-line no-await-in-loop
      await streamToFileHandle(item.url, fileHandle, storeItem.id, controller);
      downloadStore.updateItem(storeItem.id, { status: 'completed' });
    } catch (e) {
      const err = e as Error;
      if (err.name === 'AbortError' || controller.signal.aborted) {
        downloadStore.updateItem(storeItem.id, { status: 'cancelled' });
      } else {
        downloadStore.updateItem(storeItem.id, {
          status: 'failed',
          error: err.message,
        });
      }
    }
  }
};

// -----------------------------------------------------------------------------
// StreamSaver 폴백 (File System Access API 미지원 브라우저).
// SW 경유로 브라우저 기본 다운로드 폴더에 저장. 페이지 이동해도 유지.
// -----------------------------------------------------------------------------

const blobFallback = async (
  opts: DownloadItem,
  itemId: string,
  controller: AbortController
): Promise<void> => {
  const response = await fetch(opts.url, { signal: controller.signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = safeFilename(opts.filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  downloadStore.updateItem(itemId, {
    receivedBytes: blob.size,
    totalBytes: blob.size,
  });
};

const streamSaverDownload = async (opts: DownloadItem): Promise<void> => {
  const controller = new AbortController();
  const item = downloadStore.createItem(opts.label, opts.filename, () => {
    controller.abort();
  });

  try {
    if (!supportsStreamSaver()) {
      downloadStore.updateItem(item.id, { status: 'downloading' });
      await blobFallback(opts, item.id, controller);
      downloadStore.updateItem(item.id, { status: 'completed' });
      return;
    }

    const response = await fetch(opts.url, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    if (!response.body) throw new Error('No response body');

    const lenHeader = response.headers.get('Content-Length');
    const totalBytes = lenHeader ? Number(lenHeader) : null;
    downloadStore.updateItem(item.id, {
      status: 'downloading',
      totalBytes,
    });

    const fileStream = streamSaver.createWriteStream(safeFilename(opts.filename), {
      size: totalBytes ?? undefined,
    });

    const writer = fileStream.getWriter();
    const reader = response.body.getReader();
    let received = 0;

    controller.signal.addEventListener('abort', () => {
      reader.cancel().catch(() => undefined);
      writer.abort().catch(() => undefined);
    });

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      // eslint-disable-next-line no-await-in-loop
      await writer.write(value);
      received += value.byteLength;
      downloadStore.updateItem(item.id, { receivedBytes: received });
    }
    await writer.close();
    downloadStore.updateItem(item.id, { status: 'completed' });
  } catch (e) {
    const err = e as Error;
    if (err.name === 'AbortError' || controller.signal.aborted) {
      downloadStore.updateItem(item.id, { status: 'cancelled' });
    } else {
      downloadStore.updateItem(item.id, {
        status: 'failed',
        error: err.message,
      });
    }
  }
};

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * 여러 파일을 한 번의 사용자 액션으로 다운로드.
 * 1) showDirectoryPicker 지원(Chrome/Edge) → 폴더 선택 → 안에 스트리밍
 * 2) 미지원(Firefox/Safari 등) → StreamSaver 개별 다운로드 (브라우저 기본 폴더)
 *
 * 폴더 지원 브라우저에서 사용자가 취소하면 아무것도 다운받지 않고 종료.
 */
export const startBatchDownload = async (items: DownloadItem[]): Promise<void> => {
  if (items.length === 0) return;

  if (supportsDirectoryPicker()) {
    // showDirectoryPicker는 사용자 gesture 유효 상태에서 즉시 호출되어야 함.
    // 호출부에서 onClick 핸들러 안에서 바로 startBatchDownload를 호출한다는 전제.
    const dirHandle = await pickDirectory();
    if (!dirHandle) return; // 사용자가 폴더 선택 취소
    downloadStore.setPanelOpen(true);
    await downloadItemsToDirectory(items, dirHandle);
    return;
  }

  // 폴더 지정 불가 브라우저 → 개별 다운로드
  downloadStore.setPanelOpen(true);
  for (const item of items) {
    // eslint-disable-next-line no-await-in-loop
    await streamSaverDownload(item);
  }
};
